/**
 * site.js — Single source of truth for ALL marketing/SEO copy on the site.
 *
 * Edit anything inside this file to update what visitors see.
 * See ./README.md for the full editing guide.
 *
 * RULES OF THUMB
 *   • Only edit values to the RIGHT of `:`, never the keys to the left.
 *   • Strings can use either "double" or 'single' quotes. If your text contains
 *     a single quote (apostrophe), use double quotes — e.g.  "can't be there".
 *   • For long paragraphs, you can break them across lines using backticks:
 *         body: `First sentence.
 *                Second sentence.`,
 *   • Don't remove array brackets [ ] or curly braces { }.
 *   • Don't change machine-y `value:` fields (e.g. "adult_child", "senior").
 *
 * Last updated: keep this date current when you make significant edits.
 */

export const siteContent = {
  /* ---------------------------------------------------------------------- */
  /*  SEO — browser tab title and meta description                          */
  /* ---------------------------------------------------------------------- */
  seo: {
    title:
      "Careable 360+ Plus — A real Health Coordinator for your parents in India",
    description:
      "Careable 360+ Plus pairs every senior with a dedicated Health Coordinator who calls, guides, and follows up. Beta launching in India. Join the waitlist.",
  },

  /* ---------------------------------------------------------------------- */
  /*  BRAND — logo name, used in Navbar + Footer                            */
  /* ---------------------------------------------------------------------- */
  brand: {
    name: "Careable",
    subname: "360+ Plus",
    logoUrl:
      "https://customer-assets.emergentagent.com/job_design-upgrade-217/artifacts/fzqlgxrv_App%20icon.png",
    logoAlt: "Careable 360+ Plus",
  },

  /* ---------------------------------------------------------------------- */
  /*  NAVBAR — top navigation                                               */
  /* ---------------------------------------------------------------------- */
  navbar: {
    /* In-page anchor links. Don't change `id`, only `label`. */
    links: [
      { id: "for-you", label: "Who It's For" },
      { id: "coordinator", label: "Coordinator" },
      { id: "features", label: "Features" },
      { id: "store", label: "Health Store" },
      { id: "how", label: "How It Works" },
      { id: "contact", label: "Contact" },
    ],
    cta: "Join Beta",
    blogLabel: "Blog",
  },

  /* ---------------------------------------------------------------------- */
  /*  HERO — top of the page (above-the-fold)                               */
  /* ---------------------------------------------------------------------- */
  hero: {
    eyebrow: "Beta launching",
    h1: {
      line1: "Care for your parents,",
      // The second line renders in italic accent colour.
      line2: "even when you can't be there.",
    },
    subhead:
      "Careable 360+ pairs every senior with a dedicated Health Coordinator — a real person who calls, guides, and checks in. Not another app notification. Real peace of mind.",
    primaryCta: "Sign up for the Beta",
    secondaryCta: "See how it works",
    disclaimer: "Not yet on Play Store or App Store · Be first in line · No credit card",

    /* The three small stats below the H1 */
    stats: [
      { v: "530M+", l: "Indians aged 40+ by 2030" },
      { v: "78%", l: "Seniors miss doses regularly" },
      { v: "8+", l: "Regional languages" },
    ],

    /* The two big cards next to / below the H1 */
    adultChildCard: {
      tag: "For Adult Children",
      title:
        "Your parents deserve someone watching over them. Even when you can't be there.",
      blurb:
        "Whether you're in New York, Dubai, or the next street — Careable 360+ keeps you genuinely connected to your parent's health, not just their last WhatsApp message.",
      bullets: [
        "Real-time alerts if a dose is missed or a vital looks abnormal",
        "View your parent's full medication schedule from anywhere",
        "A coordinator you know by name — a real person you can reach",
        "Order medicines and supplies on their behalf, delivered home",
      ],
      cta: "Share With Your Parent",
      imageAlt: "For Adult Children",
    },
    seniorCard: {
      tag: "For Seniors",
      title: "Managing your health just got simpler, warmer, and easier.",
      blurb:
        "Your personal Health Coordinator calls you, helps set up your medicines, and reminds you about doctor visits — in your own language. No tech skills needed.",
      bullets: [
        "Loud, clear reminders for every dose",
        "Track your sugar, BP, and weight with one tap",
        "Health records safe in one place — shareable with your doctor",
        "One-touch SOS that alerts your family instantly",
      ],
      cta: "Reserve a Beta Invite",
      imageAlt: "For Seniors",
    },
  },

  /* ---------------------------------------------------------------------- */
  /*  PARTNERS — auto-scrolling strip                                       */
  /* ---------------------------------------------------------------------- */
  partners: {
    eyebrow: "Trusted Partners",
    items: [
      { iconKey: "shield", label: "Tata 1mg", sub: "Medicines & Health Commerce" },
      { iconKey: "building", label: "DDRC", sub: "Lab Network Across Kerala" },
      { iconKey: "phone", label: "Beta on Android", sub: "Coming Soon to Play Store" },
      { iconKey: "shield", label: "Tata 1mg", sub: "Medicines & Health Commerce" },
      { iconKey: "building", label: "DDRC", sub: "Lab Network Across Kerala" },
      { iconKey: "phone", label: "Beta on iOS", sub: "Coming Soon to App Store" },
    ],
  },

  /* ---------------------------------------------------------------------- */
  /*  AUDIENCE — "Who is Careable 360+ for?"                                */
  /* ---------------------------------------------------------------------- */
  audience: {
    eyebrow: "Who Is Careable 360+ For?",
    headingLead: "Built for every side of",
    headingItalic: "the care relationship",
    adultChild: {
      tag: "If You're an Adult Child",
      title: "You love your parents. You just can't be there every day.",
      body:
        "Whether you're in Bengaluru, Dubai, or the next street — Careable 360+ keeps you genuinely connected to your parent's health, not just their last WhatsApp message.",
      bullets: [
        "Real-time alerts if a dose is missed or a vital looks abnormal",
        "View your parent's full medication schedule from anywhere",
        "A coordinator you know by name — a real person you can reach",
        "Order medicines and health supplies on their behalf, delivered home",
        "Share medical records with their doctor in seconds",
      ],
      cta: "Share With Your Parent",
    },
    senior: {
      tag: "If You're a Senior",
      title: "Your health, in your hands — with someone always beside you.",
      body:
        "You don't need to be a technology expert. Your Health Coordinator calls you, helps set up everything, and checks in regularly — in Malayalam, Hindi, Tamil, or whatever language feels like home.",
      bullets: [
        "Never miss a medicine — loud, clear reminders every day",
        "Your coordinator sets up all medicines over the phone for you",
        "Track your sugar, BP, and weight with one tap",
        "All health records safe in one place — shareable with your doctor",
        "One-touch SOS that alerts your family instantly in an emergency",
      ],
      cta: "Reserve a Beta Invite",
    },
  },

  /* ---------------------------------------------------------------------- */
  /*  COORDINATOR — "Every senior gets a real person in their corner"       */
  /* ---------------------------------------------------------------------- */
  coordinator: {
    eyebrow: "What Makes Us Different",
    headingLead: "Every senior gets a",
    headingItalic: "real person",
    headingTail: "in their corner.",
    intro:
      "Most health apps send a notification and call it care. We don't. Every Careable 360+ user is paired with a dedicated Personal Health Coordinator — a trained professional who calls, listens, and follows up. Not once. Regularly.",
    intro2:
      "The single biggest reason seniors don't manage their health well isn't lack of technology. It's lack of someone who actually checks in.",
    cta: "Meet Your Coordinator",
    steps: [
      {
        iconKey: "phone",
        title: "Personal welcome call",
        body:
          "Your coordinator calls to introduce themselves and welcome your parent — by name, in their own language.",
      },
      {
        iconKey: "clipboard",
        title: "Medicine setup over the phone",
        body:
          "No typing. No confusion. Your coordinator adds every medicine, dosage, and schedule just by talking to the senior.",
      },
      {
        iconKey: "bell",
        title: "Proactive refill & appointment reminders",
        body:
          "Before medicines run out or a doctor visit slips by — your coordinator calls to remind, not just the app.",
      },
      {
        iconKey: "lightbulb",
        title: "Condition-aware product guidance",
        body:
          "Based on logged health conditions, your coordinator recommends the right health products — informed guidance, not advertising.",
      },
      {
        iconKey: "refresh",
        title: "Re-engagement when things slip",
        body:
          "If your parent goes quiet for a few days, the coordinator follows up — a real check-in, not an automated push notification.",
      },
    ],
  },

  /* ---------------------------------------------------------------------- */
  /*  APP SHOWCASE — "See the App in Action"                                */
  /* ---------------------------------------------------------------------- */
  appShowcase: {
    eyebrow: "See the App in Action",
    headingLead: "Designed for seniors.",
    headingItalic: "Built for simplicity.",
    body:
      "One glance tells your parent everything — today's blood glucose, medication progress, and quick access to every health service they need. Clean, large, and friendly for any age.",
    bullets: [
      { iconKey: "activity", label: "Live blood glucose and vitals on the home screen" },
      { iconKey: "pill", label: "Medication progress tracker — visible at a glance" },
      { iconKey: "zap", label: "Quick actions: Doctor, Lab, Store, Services" },
      { iconKey: "sos", label: "Always-visible SOS button — one hold alerts family" },
    ],
    imageAlt: "Careable 360+ Plus app home screen",
  },

  /* ---------------------------------------------------------------------- */
  /*  FEATURES — six product features                                       */
  /* ---------------------------------------------------------------------- */
  features: {
    eyebrow: "Core Features",
    headingLead: "Everything in one place.",
    headingItalic: "All of it free during beta.",
    subhead:
      "Every feature is included at no cost during our beta — no subscription, no hidden charges.",
    badge: "Free in Beta",
    items: [
      {
        iconKey: "pill",
        title: "Smart Medication Tracker",
        body:
          "Loud, clear reminders for every dose. One-tap logging. Real-time pill inventory with low-stock alerts. Weekly adherence reports visible to the whole family.",
      },
      {
        iconKey: "camera",
        title: "AI Prescription Setup",
        body:
          "Snap a photo of any medicine strip or prescription. AI reads and sets up name, dosage, and schedule automatically.",
      },
      {
        iconKey: "activity",
        title: "Health Vitals Monitoring",
        body:
          "Log Blood Glucose, BP, Weight, and BMI with one tap. View 7-day trend graphs. Automatic WATCH alerts when a reading looks abnormal.",
      },
      {
        iconKey: "folder",
        title: "Digital Health Records",
        body:
          "Upload and organise lab reports, prescriptions, discharge summaries, and hospital cards. Share with any specialist in seconds.",
      },
      {
        iconKey: "users",
        title: "Family Caregiver Dashboard",
        body:
          "Connect via a secure link. Monitor adherence, vitals, and records remotely. Real-time alerts when something needs attention.",
      },
      {
        iconKey: "sos",
        title: "Emergency SOS",
        body:
          "A large, always-visible SOS button. Hold 5 seconds to share live location with family, trigger an alarm, and connect to hospitals and 108/112 instantly.",
      },
    ],
  },

  /* ---------------------------------------------------------------------- */
  /*  HEALTH STORE                                                          */
  /* ---------------------------------------------------------------------- */
  healthStore: {
    eyebrow: "Health Store",
    headingLead: "A complete health store,",
    headingItalic: "personalised for your parent's condition.",
    body:
      "Browse, order, and receive health products at home. Powered by Tata 1mg. Your coordinator recommends what each senior actually needs.",

    categoriesLabel: "What We Carry",
    categories: [
      { iconKey: "syringe", name: "Diabetic Care", desc: "Glucometers, test strips, insulin accessories" },
      { iconKey: "heartpulse", name: "Cardiac & BP Management", desc: "BP monitors, pulse oximeters, cardiac aids" },
      { iconKey: "bone", name: "Mobility & Orthopaedic", desc: "Walking aids, supports, pain relief" },
      { iconKey: "spray", name: "Personal Care & Hygiene", desc: "Senior-specific daily essentials" },
      { iconKey: "apple", name: "Nutrition & Supplements", desc: "Senior dietary supplements & vitamins" },
      { iconKey: "pill", name: "OTC Products", desc: "Prescription refills delivered home via 1mg" },
    ],

    recsEyebrow: "Coordinator-Guided Recommendations",
    recsTitle:
      "Not advertising. Informed guidance based on your parent's actual health profile.",
    recsBody:
      "Your coordinator sees the same health data you do. When they see a pattern, they recommend the right product — not the bestseller, the right one.",
    recs: [
      { c: "Diabetic", p: "Right glucometer + test strips" },
      { c: "Hypertension", p: "BP monitor + low-sodium dietary aids" },
      { c: "Low stock alert", p: "Reorder before medicines run out" },
      { c: "Mobility concern", p: "Walking aids + bathroom safety" },
    ],

    bundleBadge: "Featured Bundle",
    bundleTitle: "Careable Home Care Kit",
    bundleBody:
      "Everything a senior needs to monitor health at home — one box, delivered to their door.",
    kitItems: ["Glucometer", "BP Monitor", "SpO2 Monitor", "Weighing Scale", "Thermometer"],
    bundlePrice: "₹4,499",
    bundlePriceNote: "Home delivery included",
    bundleCta: "Reserve in Beta",
  },

  /* ---------------------------------------------------------------------- */
  /*  SOS — Emergency SOS section                                           */
  /* ---------------------------------------------------------------------- */
  sos: {
    eyebrow: "Emergency SOS",
    headingLead: "When every second matters,",
    headingItalic: "one hold is all it takes.",
    body:
      "The Careable SOS button is always visible, anywhere in the app. Hold it for 5 seconds and your family is alerted instantly — with your parent's live location, medical profile, and a direct line to emergency services.",
    bullets: [
      { iconKey: "shield", t: "Emergency alarm triggered immediately" },
      { iconKey: "map", t: "Live location shared with family via App" },
      { iconKey: "file", t: "Medical snapshot and current medicines visible to responders" },
      { iconKey: "phone", t: "One-tap call option to nearby hospitals, ambulance & 108/112" },
    ],
    holdLabel: "Hold 5 seconds",
    footerLine: "Always visible. Always one hold away.",
  },

  /* ---------------------------------------------------------------------- */
  /*  HOW IT WORKS — five-step flow                                         */
  /* ---------------------------------------------------------------------- */
  howItWorks: {
    eyebrow: "How It Works",
    headingLead: "From sign-up to",
    headingItalic: "fully cared for",
    headingTail: "in five steps.",
    subhead: "You join the beta. We take care of everything else.",
    steps: [
      { iconKey: "download", title: "You download the app", body: "The adult child downloads Careable 360+ and taps 'Set up for my parent'" },
      { iconKey: "message", title: "Parent gets a heads-up", body: "A WhatsApp message tells your parent who will call, when, and by name — in their language" },
      { iconKey: "phone", title: "Coordinator calls", body: "Your coordinator calls, introduces themselves, and sets up all medicines over the phone" },
      { iconKey: "heart", title: "Health tracking begins", body: "Reminders, vitals, records, and the caregiver dashboard all activate automatically" },
      { iconKey: "repeat", title: "Ongoing care, always", body: "Your coordinator checks in regularly — for refills, visits, and anything needing a human touch" },
    ],
  },

  /* ---------------------------------------------------------------------- */
  /*  TESTIMONIALS — empty-state, share modal, success state                */
  /* ---------------------------------------------------------------------- */
  testimonials: {
    eyebrowEmpty: "Early Beta · Be the First",
    eyebrowFilled: "Early User Voices",

    /* Heading shown when there ARE NO approved testimonials yet */
    headingEmptyLead: "No reviews yet —",
    headingEmptyItalic: "your voice opens this page",

    /* Heading shown once at least one testimonial has been approved */
    headingFilledLead: "What families are",
    headingFilledItalic: "saying",

    intro:
      "We're in early beta. We'd rather tell you the truth than borrow strangers' words. If you've tried Careable 360+ Plus,",
    introHighlight: "your honest rating",
    introTail: "helps the next family decide.",

    loadingLabel: "Loading stories…",
    shareCta: "Share your experience",

    emptyBadge: "Honest beta status",
    emptyTitle: "We haven't collected any testimonials yet.",
    emptyBody:
      "Careable 360+ Plus is still in early beta in India. Rather than fill this page with stock reviews, we're keeping it blank until real families share their story. If you've spoken to our Health Coordinator — even for a single call — your rating belongs here.",
    emptyBullets: [
      "Takes under 60 seconds",
      "You control what gets shown",
      "Reviewed before going live",
    ],
    emptyCta: "Be the first to share your experience",
    emptyOrLineBefore: "Or write to ",
    emptyOrEmail: "hello@careable360.in",
    emptyOrLineAfter: ".",

    /* Share modal copy */
    modal: {
      eyebrow: "Share your story",
      title: "Rate Careable 360+ Plus",
      closeAria: "Close",
      ratingLabel: "Overall rating",
      ratingPrompt: "Tap to rate",
      nameLabel: "Your name *",
      namePlaceholder: "e.g., Kavya R.",
      relationLabel: "You are *",
      cityLabel: "City (optional)",
      cityPlaceholder: "e.g., Bengaluru, Kochi, Dubai",
      quoteLabel: "Your experience *",
      quotePlaceholder:
        "What did your Health Coordinator do for you or your parent? Be as specific as you can.",
      quoteMinHint: "Min 10 characters.",
      consent:
        "I confirm this is my honest experience and I consent to Careable publishing my first name, relation, and (if I provided one) city alongside this review, after moderation.",
      submit: "Submit testimonial",
      submitting: "Submitting…",
      cancel: "Cancel",
      successTitle: "Thank you for sharing.",
      successBody:
        "Your testimonial is awaiting review. We read every submission and only publish those that reflect a real experience. We'll be in touch if we need to clarify anything.",
      successCta: "Done",
    },

    /* Relation dropdown options. Don't change `value`, only `label`. */
    relationOptions: [
      { value: "adult_child", label: "Adult child of a senior" },
      { value: "senior", label: "Senior / Parent" },
      { value: "caregiver", label: "Caregiver / Family member" },
      { value: "other", label: "Other" },
    ],
    /* Short labels used on the rendered testimonial card, keyed by `value` above */
    relationShortLabel: {
      adult_child: "Adult child",
      senior: "Senior / Parent",
      caregiver: "Caregiver / Family",
      other: "Family member",
    },
  },

  /* ---------------------------------------------------------------------- */
  /*  BETA SIGNUP                                                           */
  /* ---------------------------------------------------------------------- */
  betaSignup: {
    eyebrow: "Beta Access",
    headingLead: "Sign up for the",
    headingItalic: "beta version",
    headingTail: ".",
    body:
      "Careable 360+ is not yet on the Play Store or App Store. Join the private beta to be one of the first families to experience a Health Coordinator who actually calls.",
    perks: [
      "Free during beta — no card, no commitment",
      "Direct line to founders and coordinators",
      "Help shape the product your parent will use",
    ],
    fields: {
      nameLabel: "Full name *",
      namePlaceholder: "e.g. Ananya Menon",
      emailLabel: "Email *",
      emailPlaceholder: "you@example.com",
      phoneLabel: "Phone (optional)",
      phonePlaceholder: "+91 9xxxxxxxxx",
      cityLabel: "City (optional)",
      cityPlaceholder: "e.g. Kochi",
      seniorLocationLabel: "Current location of senior / parent *",
      seniorLocationPlaceholder: "Select a state or union territory",
      roleLabel: "I'm signing up as",
    },
    /* Full list of Indian states + union territories. Don't reorder unless you
       want to change the dropdown order. The values here must match exactly
       what the backend allows (see INDIA_STATES_AND_UTS in server.py). */
    seniorLocations: [
      { group: "States", items: [
        "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
        "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
        "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
        "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
        "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
      ]},
      { group: "Union Territories", items: [
        "Andaman and Nicobar Islands", "Chandigarh",
        "Dadra and Nagar Haveli and Daman and Diu",
        "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
      ]},
    ],
    /* Don't change `v` (database key), only `l` (label shown) */
    roles: [
      { v: "adult_child", l: "Adult Child" },
      { v: "senior", l: "Senior / Parent" },
      { v: "caregiver", l: "Caregiver / Family" },
    ],
    /* Consent line. The {privacy} and {terms} placeholders are replaced with
       the linked Privacy Notice and Terms of Service. Keep them in the string. */
    consentPrefix: "I have read and accept the ",
    consentPrivacyLabel: "Privacy Notice",
    consentMiddle: " (DPDP Act, 2023) and ",
    consentTermsLabel: "Terms of Service",
    consentTail:
      ". I consent to Careable processing my personal data for the beta programme.",
    submit: "Reserve my beta invite",
    submitting: "Reserving your spot…",
    submitFooter: "We'll only email you about your beta invite. No spam, ever.",
    errors: {
      missingFields: "Please share your name and email.",
      missingConsent:
        "Please accept the Privacy Notice and Terms of Service to continue.",
      fallback: "Something went wrong. Please try again in a moment.",
    },
    success:
      "You're on the list. We'll email when your beta invite is ready — usually within 7 days.",
  },

  /* ---------------------------------------------------------------------- */
  /*  CONTACT                                                               */
  /* ---------------------------------------------------------------------- */
  contact: {
    eyebrow: "Get in Touch",
    headingLead: "Have a question?",
    headingItalic: "We'd love to hear from you.",
    body:
      "For partnerships, press, hospital tie-ups, or any question about the beta — drop us a message. Real humans read every email.",

    /* These three appear next to the form. Edit value/label as needed. */
    details: {
      emailLabel: "Email",
      email: "hello@careable360plus.com",
      phoneLabel: "Call us",
      phone: "+91 9422799196",
      addressLabel: "Based in",
      address: "Kochi, Kerala · India",
    },

    fields: {
      nameLabel: "Name *",
      namePlaceholder: "Your full name",
      emailLabel: "Email *",
      emailPlaceholder: "you@example.com",
      subjectLabel: "Subject",
      subjectPlaceholder: "Partnership · Press · Beta · Other",
      messageLabel: "Message *",
      messagePlaceholder: "Tell us a bit about what you're looking for…",
    },

    submit: "Send message",
    submitting: "Sending…",
    errors: {
      missingFields: "Name, email and message are required.",
      fallback: "Something went wrong. Please try again later.",
    },
    success: "Thank you. We'll get back to you within 24 hours.",
  },

  /* ---------------------------------------------------------------------- */
  /*  FOOTER                                                                */
  /* ---------------------------------------------------------------------- */
  footer: {
    tagline:
      "A senior health companion built for India — pairing every family with a real Health Coordinator who calls, listens, and follows up.",
    cta: "Join the Beta",
    columns: [
      {
        title: "Product",
        links: [
          { l: "Features", id: "features" },
          { l: "How it works", id: "how" },
          { l: "Health Store", id: "store" },
          { l: "Emergency SOS", id: "sos" },
        ],
      },
      {
        title: "Company",
        links: [
          { l: "Who it's for", id: "for-you" },
          { l: "Coordinator", id: "coordinator" },
          { l: "Testimonials", id: "testimonials" },
          { l: "Contact", id: "contact" },
        ],
      },
      {
        title: "Legal",
        type: "routes",
        links: [
          { l: "Privacy Notice", to: "/terms-and-policies#privacy-notice", id: "privacy-notice" },
          { l: "Privacy Policy", to: "/terms-and-policies#privacy-policy", id: "privacy-policy" },
          { l: "Terms of Service", to: "/terms-and-policies#terms-of-service", id: "terms-of-service" },
        ],
      },
    ],
    partnersTitle: "Partners",
    partnersItems: ["Tata 1mg", "DDRC Aglius", "Coming to Play Store", "Coming to App Store"],
    copyrightHolder: "Careable 360+ Plus. All rights reserved.",
    signoff: "Built with care for families across India.",
    blogLabel: "Blog",
    socialTitle: "Follow us",
    /* Social profiles shown in the footer. Replace the "#" with real URLs when ready. */
    social: [
      { key: "instagram", label: "Instagram", url: "#" },
      { key: "facebook", label: "Facebook", url: "#" },
      { key: "twitter", label: "X (Twitter)", url: "#" },
      { key: "youtube", label: "YouTube", url: "#" },
    ],
  },
};

export default siteContent;
